import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db, auth, secondaryAuth } from '../firebase';
import { collection, doc, setDoc, serverTimestamp, query, where, onSnapshot } from 'firebase/firestore';
import { format } from 'date-fns';
import { 
  ShieldCheck, ChevronLeft, Users, 
  Lock, AlertCircle, Eye, EyeOff, 
  Fingerprint, Zap, Activity, FileDown, 
  UserPlus, Database,
  Settings2,
  ChevronRight,
  Gavel,
  Search
} from 'lucide-react';

import { createUserWithEmailAndPassword, signOut } from 'firebase/auth';


const RoleManager = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [timeRange, setTimeRange] = useState('ALL');

  const [members, setMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    role: "Operator",
    ndtLevel: "Level II",
    department: "",
    accessLevel: "standard",
  });

// Note: Ensure you have initialized a 'secondaryAuth' if doing this purely client-side
// or use the logic below which handles the record creation.
useEffect(() => {
  if (!auth.currentUser) return;

  console.log("ddd",auth.currentUser.uid)
  // Reference to the main users collection
  const usersRef = collection(db, "users");

  // Query: Find all users where orgUid matches the current Admin's UID
  const q = query(
    usersRef, 
    where("orgUid", "==", auth.currentUser.uid)
  );

  // Subscribe to real-time updates
  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    const membersList = [];
    querySnapshot.forEach((doc) => {
      membersList.push({ id: doc.id, ...doc.data() });
    });
    setMembers(membersList);
  }, (error) => {
    console.error("Registry Fetch Error:", error);
  });

  return () => unsubscribe();
}, [auth.currentUser]);


const handleProvisioning = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError("");

  try {
    // 1. Capture the Admin context from the MAIN auth instance
    const adminUid = auth.currentUser.uid;
    const adminEmail = auth.currentUser.email;

    // 2. Create the user using the SECONDARY instance
    // This will NOT log you out of the main dashboard
    const userCredential = await createUserWithEmailAndPassword(
      secondaryAuth, 
      formData.email, 
      formData.password
    );
    
    const newMemberUid = userCredential.user.uid;

    // 3. Construct the Registry Entry
    const memberData = {
      uid: newMemberUid,
      fullName: formData.fullName,
      email: formData.email,
      role: formData.role,
      ndtLevel: formData.ndtLevel,
      department: formData.department,
      orgUid: adminUid, 
      provisionedBy: adminEmail,
      status: "active",
      createdAt: serverTimestamp(),
      complianceVerified: true
    };

    // 4. Save to Firestore using the main DB instance
    await setDoc(doc(db, "users", newMemberUid), memberData);

    // 5. CRITICAL: Sign out of the secondary instance only
    // This cleans up the temporary session without touching your Admin login
    await signOut(secondaryAuth);
    

    alert(`Registry Sync Complete: ${formData.fullName} added to Focus NDT Group.`);
    

setFormData({
  fullName: "",
    email: "",
    password: "",
    role: "Operator",
    ndtLevel: "Level II",
    department: "",
    accessLevel: "standard",
});


  } catch (err) {
    console.error("Registry Sync Error:", err);
    setError(err.message);
  } finally {
    setLoading(false);
  }
};

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 flex flex-col p-0 md:p-4">
      <div className="flex-1 flex flex-col bg-white border border-slate-300 shadow-2xl overflow-hidden">
        
        {/* TOP LEVEL: STATUTORY HEADER */}
        <div className="flex flex-col md:flex-row items-stretch justify-between border-b border-slate-300">
          
          {/* OPERATIONAL IDENTITY */}
          <div className="p-8 border-r border-slate-200 flex-1">
            <div className="flex items-baseline gap-4 mb-6">
              <h2 className="text-3xl font-serif font-black text-blue-900 uppercase tracking-tight">
                Workforce Registry
              </h2>
              <span className="text-xs font-mono text-slate-500 font-bold border-l border-slate-300 pl-3 uppercase">
                Auth-v8.1.0
              </span>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 items-center">
              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Admin Status</p>
                <div className="flex items-center gap-2">
                  <div className="h-2 w-2 bg-emerald-600 rounded-full animate-pulse" />
                  <span className="text-[11px] font-bold text-slate-700 uppercase tracking-tight">Secure Session Active</span>
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Master Admin ID</p>
                <p className="text-[11px] font-bold text-blue-900 uppercase">
                  ADM-{auth.currentUser?.uid?.slice(0, 8).toUpperCase()}
                </p>
              </div>

              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Node Location</p>
                <p className="text-[11px] font-bold text-slate-700 uppercase">Focus NDT Group / HQ</p>
              </div>

              <div className="space-y-1">
                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Timestamp (IST)</p>
                <p className="text-[11px] font-bold text-slate-700 uppercase font-mono">
                  {format(new Date(), "yyyy-MM-dd | HH:mm")}
                </p>
              </div>
            </div>
          </div>

          {/* PRIMARY EXIT CONTROL */}
          <div className="bg-slate-50 p-8 flex flex-col justify-center gap-3 border-l border-slate-200">
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center justify-center gap-4 px-10 py-4 bg-slate-900 text-white font-black text-[12px] uppercase tracking-[0.2em] hover:bg-blue-900 transition-all shadow-lg active:scale-95"
            >
              <ChevronLeft size={14} className="text-slate-400" />
              Return to Account
            </button>
            <p className="text-[9px] text-center text-slate-500 font-bold uppercase tracking-widest">
              System: Focus Core Engine
            </p>
          </div>
        </div>

        {/* SECTION 1B: METRICS BAR */}
     <div className="flex flex-wrap items-center justify-between bg-slate-50 p-6 border-b border-slate-300">
  <div className="flex items-center gap-10">
    
    {/* PRIMARY STATS: TOTAL FORCE */}
    <div className="flex items-center gap-4">
      <div className="p-3 bg-blue-900 text-white">
        <Users size={20} />
      </div>
      <div className="flex flex-col">
        <span className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em]">Total Personnel Force</span>
        <div className="flex items-baseline gap-2">
          <span className="text-xl font-black text-slate-900 leading-none">
            {members.length.toString().padStart(2, '0')}
          </span>
          <span className="text-[10px] font-bold text-slate-400 uppercase italic">Registered Units</span>
        </div>
      </div>
    </div>

    <div className="h-12 w-px bg-slate-300" />

    {/* SYSTEM STATUS & COMPLIANCE */}
    <div className="flex gap-12">
      <div className="flex flex-col">
        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Legal Jurisdiction</span>
        <div className="flex items-center gap-2">
          <div className="h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.6)]" />
          <span className="text-[11px] font-black text-slate-900 uppercase">ASNT / ISO Compliant</span>
        </div>
      </div>

      <div className="flex flex-col">
        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Registry Integrity</span>
        <div className="flex items-center gap-2 text-blue-900">
          <ShieldCheck size={14} />
          <span className="text-[11px] font-black uppercase">Statutory Encrypted</span>
        </div>
      </div>

      <div className="flex flex-col">
        <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Audit Trail</span>
        <span className="text-[11px] font-black text-slate-900 uppercase italic">Live Logging...</span>
      </div>
    </div>
  </div>

  {/* PROFESSIONAL ACTIONS */}
  <div className="flex items-center gap-4">
    <div className="flex flex-col items-end mr-2">
       <span className="text-[8px] font-black text-slate-400 uppercase">Last Registry Sync</span>
       <span className="text-[10px] font-bold text-slate-900">{new Date().toLocaleTimeString()}</span>
    </div>
    <button className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-slate-900 text-[10px] font-black text-slate-900 uppercase tracking-wider hover:bg-slate-900 hover:text-white transition-all">
      <Gavel size={14}/> Legal Review
    </button>
  </div>
</div>

        {/* SECTION 2: FORM ENGINE (FULL SCREEN) */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden bg-white">
          
          {/* LEFT: INPUT AREA */}
    <form onSubmit={handleProvisioning} className="lg:col-span-8 p-10 md:p-16 overflow-y-auto space-y-12">
    
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
      {/* Identity Section */}
      <div className="space-y-6">
         <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-blue-900 border-b-2 border-blue-900 pb-2 flex items-center gap-3">
           <UserPlus size={14}/> 01. Personnel Data
         </h4>
         
         <div className="space-y-2">
            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Full Legal Name</label>
            <input 
              required 
              type="text" 
              placeholder="SURNAME, FIRST NAME" 
              value={formData.fullName || ""}
              className="w-full bg-slate-50 border border-transparent rounded-none p-4 font-bold text-sm outline-none transition-all placeholder:text-slate-300 focus:ring-1 focus:ring-blue-900 focus:bg-white focus:border-blue-900"
              onChange={e => setFormData({...formData, fullName: e.target.value})} 
            />
         </div>

         <div className="space-y-2">
            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Login Email</label>
            <input 
              required 
              type="email" 
              placeholder="DEPT@FOCUSNDT.IN" 
              value={formData.email || ""}
              className="w-full bg-slate-50 border border-transparent rounded-none p-4 font-bold text-sm outline-none transition-all placeholder:text-slate-300 focus:ring-1 focus:ring-blue-900 focus:bg-white focus:border-blue-900"
              onChange={e => setFormData({...formData, email: e.target.value})} 
            />
         </div>

         <div className="space-y-2 relative">
            <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Access Credentials</label>
            <input 
              required 
              type={showPassword ? "text" : "password"} 
              placeholder="••••••••" 
              value={formData.password || ""}
              className="w-full bg-slate-50 border border-transparent rounded-none p-4 font-bold text-sm outline-none transition-all placeholder:text-slate-300 focus:ring-1 focus:ring-blue-900 focus:bg-white focus:border-blue-900"
              onChange={e => setFormData({...formData, password: e.target.value})} 
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-10 text-slate-400">
              {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
            </button>
         </div>
      </div>

      {/* Technical Section */}
      <div className="space-y-6">
         <h4 className="text-[11px] font-black uppercase tracking-[0.2em] text-blue-900 border-b-2 border-blue-900 pb-2 flex items-center gap-3">
           <Activity size={14}/> 02. Technical Spec
         </h4>

         <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">NDT Designation</label>
              <select 
                value={formData.role}
                className="w-full bg-slate-50 border-none rounded-none p-4 font-bold text-sm outline-none cursor-pointer focus:ring-1 focus:ring-blue-900"
                onChange={e => setFormData({...formData, role: e.target.value})}>
                <option value="Operator">Operator</option>
                <option value="Supervisor">Supervisor</option>
                <option value="Auditor">Auditor</option>
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Cert Level</label>
              <select 
                value={formData.ndtLevel}
                className="w-full bg-slate-50 border-none rounded-none p-4 font-bold text-sm outline-none cursor-pointer focus:ring-1 focus:ring-blue-900"
                onChange={e => setFormData({...formData, ndtLevel: e.target.value})}>
                <option value="Level I">Level I</option>
                <option value="Level II">Level II</option>
                <option value="Level III">Level III</option>
              </select>
            </div>
         </div>

         <div className="space-y-2">
          <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
            Primary Department
          </label>
          <input 
            type="text" 
            placeholder="PAUT / UT / RADIOGRAPHY" 
            value={formData.department || ""} 
            className="w-full bg-slate-50 border border-transparent p-4 font-bold text-sm outline-none placeholder:text-slate-300 transition-all focus:ring-1 focus:ring-blue-900 focus:bg-white focus:border-blue-900"
            onChange={e => setFormData({...formData, department: e.target.value})} 
          />
        </div>
      </div>
    </div>

    {/* FORM FOOTER */}
    <div className="pt-12 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-4 text-slate-400">
         <ShieldCheck size={20} className="text-blue-900" />
         <p className="text-[9px] font-black uppercase tracking-widest leading-tight">
           Authorization Level II Verified <br/>
           <span className="text-slate-300 italic">Encrypted via RSA-4096 Protocol</span>
         </p>
      </div>

      <button 
        type="submit" 
        disabled={loading}
        className="group flex items-center justify-center gap-4 px-12 py-5 bg-blue-900 text-white font-black text-[12px] uppercase tracking-[0.2em] hover:bg-blue-950 transition-all shadow-xl disabled:bg-slate-300"
      >
        <Zap size={14} className="text-amber-400 group-hover:animate-bounce" />
        {loading ? "INITIALIZING..." : "Commit Member to Registry"}
      </button>
    </div>
  </form>


          {/* RIGHT: OPERATIONAL PERSONNEL INDEX */}
{/* RIGHT: OPERATIONAL PERSONNEL INDEX */}
{/* RIGHT: OPERATIONAL PERSONNEL INDEX */}
<div className="lg:col-span-4 bg-slate-50 border-l border-slate-300 p-8 flex flex-col h-[calc(100vh-180px)] overflow-hidden">
  
  {/* SECTION HEADER - PINNED */}
  <div className="flex items-center justify-between mb-8 shrink-0">
    <div className="space-y-1">
      <h4 className="text-[11px] font-black uppercase tracking-[0.3em] text-slate-400">
        Personnel Index
      </h4>
      <p className="text-[9px] font-bold text-blue-900/60 uppercase tracking-widest">
        Focus NDT Group / {members.length} Active Records
      </p>
    </div>
    <Database size={18} className="text-slate-300" />
  </div>

  {/* DYNAMIC SEARCH - PINNED */}
  <div className="mb-6 shrink-0">
    <div className="relative group">
      <input 
        type="text" 
        placeholder="SEARCH REGISTRY (NAME / ROLE / UID)..." 
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="w-full bg-white border border-slate-200 p-4 pl-12 text-[10px] font-bold uppercase tracking-widest outline-none focus:ring-1 ring-blue-900 focus:border-blue-900 transition-all placeholder:text-slate-300"
      />
      <Search size={16} className={`absolute left-4 top-4 transition-colors ${searchTerm ? 'text-blue-900' : 'text-slate-300'}`} />
      {searchTerm && (
        <button 
          onClick={() => setSearchTerm("")}
          className="absolute right-4 top-4 text-[9px] font-black text-slate-400 hover:text-red-600 uppercase"
        >
          Clear
        </button>
      )}
    </div>
  </div>

  {/* SCROLLABLE LIST AREA - DYNAMIC */}
  <div className="flex-1 overflow-y-auto space-y-3 pr-2 custom-scrollbar">
    {members.filter(m => 
      m.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.id?.toLowerCase().includes(searchTerm.toLowerCase())
    ).length > 0 ? (
      members
        .filter(m => 
          m.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          m.role?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          m.id?.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .map((member) => (
          <Link 
            key={member.id} 
            to={`/dashboard/account/rolemanager/member/${member.id}`}
            className="group flex flex-col bg-white border border-slate-200 p-5 hover:border-blue-900 transition-all shadow-sm hover:shadow-md relative overflow-hidden"
          >
            {/* Status Indicator Lip */}
            <div className="absolute top-0 left-0 w-1 h-full bg-slate-200 group-hover:bg-blue-900 transition-colors" />
            
            <div className="flex justify-between items-start mb-3">
              <div className="space-y-1">
                <span className="text-[12px] font-black text-slate-900 uppercase tracking-tight block group-hover:text-blue-900 transition-colors">
                  {member.fullName}
                </span>
                <span className="text-[9px] font-mono text-slate-400">
                  UID: {member.id.slice(0, 14).toUpperCase()}
                </span>
              </div>
              <div className="p-2 bg-slate-50 group-hover:bg-blue-50 transition-colors">
                <Settings2 size={14} className="text-slate-400 group-hover:text-blue-900" />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[8px] font-black bg-blue-900 text-white px-2 py-0.5 uppercase tracking-tighter">
                {member.role}
              </span>
              <span className="text-[8px] font-black border border-blue-900 text-blue-900 px-2 py-0.5 uppercase tracking-tighter">
                {member.ndtLevel}
              </span>
              <div className="ml-auto flex items-center gap-1.5">
                <div className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-[8px] font-black text-slate-400 uppercase">Live Auth</span>
              </div>
            </div>
          </Link>
        ))
    ) : (
      <div className="h-full flex flex-col items-center justify-center border-2 border-dashed border-slate-200 p-10 text-center">
        <div className="p-4 bg-slate-100 rounded-full mb-4">
          <Database size={24} className="text-slate-300" />
        </div>
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] leading-relaxed">
          {searchTerm ? `No match for "${searchTerm}"` : "Registry Empty"} <br/>
          <span className="text-slate-300 font-bold">Node: Focus NDT Group</span>
        </p>
      </div>
    )}
  </div>

  {/* SYSTEM ACTIONS - PINNED */}
  <div className="mt-8 pt-6 border-t border-slate-200 space-y-3 shrink-0">
    <button className="w-full group flex items-center justify-between bg-white border border-slate-300 text-slate-500 px-6 py-4 text-[10px] font-black uppercase tracking-[0.15em] hover:bg-slate-900 hover:text-white transition-all">
      <span className="flex items-center gap-3">
        <FileDown size={14} className="group-hover:translate-y-0.5 transition-transform" />
        Export Registry SOP
      </span>
      <span className="text-[8px] opacity-40 italic">PDF</span>
    </button>

  </div>
</div>


        </div>

        {/* OPERATIONAL GUIDELINES: FULL-WIDTH BOTTOM SECTION */}
<section className="w-full bg-slate-50 border-t border-slate-300 p-8 md:p-12">
  <div className="max-w-7xl mx-auto">
    {/* HEADER */}
    <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-slate-400 mb-10 text-center md:text-left">
      Operational Guidelines & Compliance
    </h4>
    
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 items-stretch">
      {/* COMPLIANCE CARD */}
      <div className="flex gap-5 p-8 bg-white border border-slate-200 shadow-sm transition-all hover:shadow-md">
        <Lock size={24} className="text-blue-900 shrink-0" />
        <div className="space-y-2">
          <p className="text-[11px] font-black uppercase tracking-widest text-blue-900">Statutory Compliance</p>
          <p className="text-[11px] leading-relaxed text-slate-500 font-bold uppercase tracking-tight">
            All provisioned members must adhere to <span className="text-slate-900">ASNT SNT-TC-1A</span> and <span className="text-slate-900">ISO 9712</span> certification standards.
          </p>
        </div>
      </div>

      {/* AUDIT CARD */}
      <div className="flex gap-5 p-8 bg-white border border-slate-200 shadow-sm transition-all hover:shadow-md">
        <AlertCircle size={24} className="text-amber-600 shrink-0" />
        <div className="space-y-2">
          <p className="text-[11px] font-black uppercase tracking-widest text-amber-600">Audit Traceability</p>
          <p className="text-[11px] leading-relaxed text-slate-500 font-bold uppercase tracking-tight">
            Every record created here is permanently linked to the <span className="text-slate-900">Focus NDT Master Registry</span> for industrial audit traceability.
          </p>
        </div>
      </div>

      {/* DOWNLOAD ACTION */}
      <div className="flex flex-col justify-center">
        <button className="w-full flex items-center justify-center gap-4 bg-white border-2 border-blue-900 text-blue-900 px-8 py-5 text-[11px] font-black uppercase tracking-[0.2em] hover:bg-blue-900 hover:text-white transition-all shadow-sm">
          <FileDown size={18} />
          Download Registry SOP
        </button>
      </div>
    </div>
  </div>
</section>
      </div>
    </div>
  );
};

export default RoleManager;